/**
 * Wires the phone app's native side (lib/mobile.ts) to the client. Does
 * nothing on the web or the desktop.
 *
 * - Voice: joining a call starts Android's voice service, named for the
 *   channel (or the DM); moving renames it; leaving stops it. Connecting
 *   asks again: Android starts it only with the microphone allowed, and the
 *   call itself asks for the microphone after the join (a first call, or a
 *   grant for "only this time").
 * - Push: once signed in, with the Terms agreed to (account/Terms.tsx), the
 *   token is registered once per session: after sign-in, and at every start
 *   while signed in. The OS's own prompt asks for the permission then.
 * - A tapped push opens its channel or DM, the one the app was opened with
 *   too, once the app is ready.
 * - Links out of the app open in the system browser (and mail app); an
 *   invite link to this server opens here. One the app can't open is copied
 *   instead (a mail link: its address), and a toast says so.
 */
import { tr } from '../i18n';
import { decodeRoute, openRoute } from '../notify/route';
import { openInvite } from '../shell/InviteLink';
import { termsAccepted, useTerms } from '../account/Terms';
import { useData } from '../store/data';
import { privateChannelName } from '../store/selectors';
import { useSession } from '../store/session';
import { toast } from '../ui/overlay';
import { useVoice } from '../voice/store';
import { copyText } from './clipboard';
import { isMobileApp, serverHost } from './config';
import {
  listenNative,
  loadAppInfo,
  openExternal,
  registerPush,
  setVoiceActive,
  takeLaunchPush,
} from './mobile';

const SNOWFLAKE = /^\d{1,20}$/;

/** A push's data (`{ type, guild_id, channel_id, message_id }`) as the route a notification opens. */
function openPush(data: unknown): void {
  if (!data || typeof data !== 'object') return;
  const d = data as Record<string, unknown>;
  const inGuild = typeof d.guild_id === 'string' && SNOWFLAKE.test(d.guild_id);
  // decodeRoute checks the ids (a malformed push opens nothing)
  const route = decodeRoute(
    JSON.stringify({
      to: inGuild ? 'channel' : 'dm',
      channelId: d.channel_id,
      messageId: typeof d.message_id === 'string' && d.message_id ? d.message_id : undefined,
    }),
  );
  if (route) openRoute(route);
}

function wireVoice(): void {
  // the app starts with no voice service
  let last = 'off';
  useVoice.subscribe((v) => {
    const s = v.session;
    const id = s.channelId ?? s.targetId;
    const active = !!id && s.status !== 'idle' && s.status !== 'failed';
    let title: string | undefined;
    if (active && id) {
      const data = useData.getState();
      const c = data.channels[id];
      // a DM call: the DM's name, as the dock says it
      title = c ? (c.guild_id ? c.name : privateChannelName(data, c)) || undefined : undefined;
    }
    // connected: the microphone is open, so the service can start now (see above)
    const now = active ? `on:${title ?? ''}:${s.status === 'connected'}` : 'off';
    if (now === last) return;
    last = now;
    setVoiceActive(active, title);
  });
}

function wirePush(): void {
  // this session's token was asked for; the launch push was taken
  let registered = false;
  let launchTaken = false;
  let waiting: unknown = null;
  const sync = () => {
    const status = useSession.getState().status;
    if (status === 'signed_out') registered = false;
    if (status !== 'ready') return;
    if (!launchTaken) {
      launchTaken = true;
      void takeLaunchPush().then((d) => d && openPush(d));
    }
    if (waiting) {
      const d = waiting;
      waiting = null;
      openPush(d);
    }
    const me = useData.getState().me;
    if (!registered && me && termsAccepted(me.id)) {
      registered = true;
      void registerPush();
    }
  };
  useSession.subscribe(sync);
  useTerms.subscribe(sync);
  listenNative<unknown>('neosocial://push-open', (d) => {
    waiting = d;
    sync();
  });
  sync();
}

const INVITE_PATH = /^\/invite\/([^/?#]+)\/?$/;

function wireLinks(): void {
  // capture: before a link's own handler stops the click (hub/markdown.tsx);
  // a long-press's click is already eaten (installLongPress, earlier)
  document.addEventListener(
    'click',
    (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      const a = (e.target as Element | null)?.closest?.('a[href]');
      if (!(a instanceof HTMLAnchorElement)) return;
      let url: URL;
      try {
        url = new URL(a.href, location.href);
      } catch {
        return;
      }
      if (!/^(https?|mailto):$/.test(url.protocol) || url.origin === location.origin) return;
      e.preventDefault();
      const invite = url.host === serverHost() ? INVITE_PATH.exec(url.pathname) : null;
      if (invite && useSession.getState().status === 'ready')
        void openInvite(decodeURIComponent(invite[1]!));
      else void openExternal(url.href).then((ok) => ok || copyInstead(url));
    },
    true,
  );
}

/** A link the app couldn't open: on the clipboard instead (a mail link's address), never a dead tap. */
function copyInstead(url: URL): void {
  const { t } = tr();
  const text = url.protocol === 'mailto:' ? decodeURIComponent(url.pathname) : url.href;
  void copyText(text).then((ok) =>
    ok
      ? toast(t('mob.link.copied', { text }), { icon: 'check' })
      : toast(t('ns.copy.failed', { text }), { icon: 'alert', tone: 'warn', duration: 9000 }),
  );
}

export function connectMobileShell(): void {
  if (!isMobileApp()) return;
  void loadAppInfo();
  wireVoice();
  wirePush();
  wireLinks();
}
